import React, { useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Instagram, MapPin, Copy, Check, ArrowRight, Zap } from 'lucide-react';
import SectionHeading from '@/components/SectionHeading';
import { CONTACT } from '@/constants/testIds';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = ['New Website', 'Redesign', 'Landing Page', 'E-commerce', 'Custom App', 'Not Sure Yet'];
const BUDGETS = ['Under ₹5K', '₹5K–₹10K', '₹10K–₹20K', '₹20K+'];

function CopyEmailButton() {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try { await navigator.clipboard.writeText('bytebitestore@gmail.com'); } catch (e) { /* noop */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <button
      data-testid={CONTACT.emailCopy}
      onClick={onClick}
      className="group flex items-center gap-3 w-full text-left px-4 py-3 rounded-xl border border-[color:var(--sh-line)] bg-white hover:border-[color:var(--sh-amber)] hover:bg-[#FFFCF4] transition-all"
    >
      <span className="w-9 h-9 rounded-lg inline-flex items-center justify-center bg-[#FFF6E5] border border-[#F5DFAE]">
        <Mail size={15} className="text-[color:var(--sh-amber-2)]" />
      </span>
      <span className="flex-1">
        <span className="block text-[10px] font-accent uppercase tracking-[0.22em] text-[color:var(--sh-muted)]">Email</span>
        <span className="block font-medium text-[14px] text-[color:var(--sh-ink)]">bytebitestore@gmail.com</span>
      </span>
      <span
        data-testid={CONTACT.emailToast}
        className="text-[10px] font-accent uppercase tracking-[0.22em] inline-flex items-center gap-1 text-[color:var(--sh-amber-2)]"
      >
        {copied ? (<><Check size={13} /> Copied!</>) : (<><Copy size={13} /> Copy Email</>)}
      </span>
    </button>
  );
}

function Field({ id, label, children }) {
  return (
    <label htmlFor={id} className="block">
      <span className="block text-[10px] font-accent uppercase tracking-[0.22em] text-[color:var(--sh-muted)] mb-2">{label}</span>
      {children}
    </label>
  );
}

export default function Contact() {
  const [form, setForm] = useState({
    full_name: '', business_name: '', email: '', phone: '',
    service_type: '', budget: '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (!form.full_name.trim()) return 'Please enter your name.';
    if (!form.email.trim()) return 'Please enter your email.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'That email looks off — check it?';
    return '';
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) return setError(v);
    setError('');
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      if (data?.success) {
        setSuccess(true);
        setForm({ full_name: '', business_name: '', email: '', phone: '', service_type: '', budget: '', message: '' });
      }
    } catch (err) {
      setError('Something went wrong on our side. Try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section" data-testid={CONTACT.block}>
      <div className="container-pro">
        <SectionHeading
          eyebrow="LET’S WORK TOGETHER"
          title="Let’s make something the internet hasn’t seen yet."
          subtitle="Fill this in and we’ll respond within 24 hours. Seriously."
        >
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-600/30 bg-emerald-50 text-emerald-800 text-[11px] font-accent uppercase tracking-[0.2em]">
            <Zap size={11} /> Avg. response under 2 hours
          </div>
        </SectionHeading>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
          <form
            data-testid={CONTACT.form}
            onSubmit={submit}
            className="lg:col-span-3 card-light p-6 md:p-8 relative"
            noValidate
          >
            <AnimatePresence mode="wait">
              {success ? (
                <motion.div
                  key="success"
                  data-testid={CONTACT.success}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-center py-10"
                >
                  <motion.svg width="68" height="68" viewBox="0 0 68 68" className="mx-auto">
                    <polygon points="34,4 60,18 60,50 34,64 8,50 8,18" fill="none" stroke="#E89A1F" strokeWidth="2" />
                    <motion.path
                      d="M22 35 L31 44 L48 26"
                      fill="none"
                      stroke="#E89A1F"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.6, ease: 'easeOut' }}
                    />
                  </motion.svg>
                  <h3 className="mt-5 font-display text-2xl text-[color:var(--sh-ink)]">We got it.</h3>
                  <p className="mt-2 text-[color:var(--sh-muted)]">You’ll hear from us within 24 hours.</p>
                  <button onClick={() => setSuccess(false)} className="btn-ghost mt-6 inline-flex items-center gap-2 text-sm">
                    Send another
                  </button>
                </motion.div>
              ) : (
                <motion.div key="form" initial={{ opacity: 1 }} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field id="full_name" label="Full Name *">
                    <input id="full_name" data-testid={CONTACT.full_name} className="field" placeholder="Your name" value={form.full_name} onChange={onChange('full_name')} />
                  </Field>
                  <Field id="business_name" label="Business Name">
                    <input id="business_name" data-testid={CONTACT.business_name} className="field" placeholder="Optional but helpful" value={form.business_name} onChange={onChange('business_name')} />
                  </Field>
                  <Field id="email" label="Email Address *">
                    <input id="email" data-testid={CONTACT.email} type="email" className="field" placeholder="you@yourdomain.com" value={form.email} onChange={onChange('email')} />
                  </Field>
                  <Field id="phone" label="Phone Number">
                    <input id="phone" data-testid={CONTACT.phone} className="field" placeholder="+91 ——" value={form.phone} onChange={onChange('phone')} />
                  </Field>
                  <Field id="service_type" label="What do you need?">
                    <select id="service_type" data-testid={CONTACT.service_type} className="field" value={form.service_type} onChange={onChange('service_type')}>
                      <option value="">Pick one</option>
                      {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field id="budget" label="Budget Range">
                    <select id="budget" data-testid={CONTACT.budget} className="field" value={form.budget} onChange={onChange('budget')}>
                      <option value="">Pick one</option>
                      {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </Field>
                  <div className="md:col-span-2">
                    <Field id="message" label="Tell us more">
                      <textarea id="message" data-testid={CONTACT.message} className="field min-h-[120px] resize-y" placeholder="What’s the project? Goals? Vibes? Spill." value={form.message} onChange={onChange('message')} />
                    </Field>
                  </div>
                  {error && <div className="md:col-span-2 text-sm text-red-600">{error}</div>}
                  <div className="md:col-span-2">
                    <button
                      type="submit"
                      data-testid={CONTACT.submit}
                      disabled={submitting}
                      className="btn-amber w-full inline-flex items-center justify-center gap-2"
                    >
                      {submitting ? 'Sending…' : 'Send It'} <ArrowRight size={15} />
                    </button>
                    <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
                      {[
                        'We read your brief within 2 hours',
                        'You get a custom proposal in 24 hours',
                        'We schedule a 15-min call before anything begins',
                      ].map((t) => (
                        <div key={t} className="flex items-start gap-2 text-[12px] text-[color:var(--sh-muted)]">
                          <Check size={13} className="text-[color:var(--sh-amber-2)] mt-0.5 shrink-0" />
                          <span>{t}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </form>

          <div className="lg:col-span-2 space-y-3 relative">
            <CopyEmailButton />
            <a
              href="https://instagram.com/sitehive.in"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border border-[color:var(--sh-line)] bg-white hover:border-[color:var(--sh-amber)] hover:bg-[#FFFCF4] transition-all"
            >
              <span className="w-9 h-9 rounded-lg inline-flex items-center justify-center bg-[#FFF6E5] border border-[#F5DFAE]">
                <Instagram size={15} className="text-[color:var(--sh-amber-2)]" />
              </span>
              <span className="flex-1">
                <span className="block text-[10px] font-accent uppercase tracking-[0.22em] text-[color:var(--sh-muted)]">Instagram</span>
                <span className="block font-medium text-[14px] text-[color:var(--sh-ink)]">@sitehive.in</span>
              </span>
              <ArrowRight size={14} className="text-[color:var(--sh-amber-2)]" />
            </a>
            <div className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border border-[color:var(--sh-line)] bg-white">
              <span className="w-9 h-9 rounded-lg inline-flex items-center justify-center bg-[#FFF6E5] border border-[#F5DFAE]">
                <MapPin size={15} className="text-[color:var(--sh-amber-2)]" />
              </span>
              <span className="flex-1">
                <span className="block text-[10px] font-accent uppercase tracking-[0.22em] text-[color:var(--sh-muted)]">Location</span>
                <span className="block font-medium text-[14px] text-[color:var(--sh-ink)]">Delhi, India 🇮🇳</span>
              </span>
            </div>
            <div className="px-4 py-3 rounded-xl border border-[color:var(--sh-line)] bg-[#FFFCF4] text-[11px] font-accent uppercase tracking-[0.22em] text-[color:var(--sh-amber-2)]">
              🇮🇳 India-based · INR pricing · No middlemen · Direct studio access
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
